import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import EditingReturnBanner from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import PageLoader from '../ui/PageLoader'
import { fetchPassSequenceImage } from '../../utils/passSequenceImagesClient'
import { expandPassSequenceToLabels } from '../../utils/passSequenceExpanded'
import { stampBevelIdsIfMissing } from '../../utils/bevelPassesIdentity'
import { migrateLegacyIdentifiersIntoPassSection } from '../../utils/wpsIdentifiersOnBevel'

/**
 * Root Misfire is no longer a selectable pass: the semi-automatic (RC) root is
 * modelled as a Root sub-tab in Weld Details and as `rootEquipment` on the
 * Floor Sheet weld. Legacy payloads that still carry it in `passSequence` are
 * left untouched so counts and downstream readers keep working.
 */
const basePassOrder = ['Root', 'Hot', 'Fill', 'Cap', 'Strip']
const LEGACY_ROOT_MISFIRE_PASS = 'Root Misfire'
const MAX_CAP_COUNT = 2
const MAX_FILL_COUNT = 21

type RootType = 'internal' | 'external'

type PassPayload = {
  /**
   * Stable per-bevel scope id. Stamped lazily on first read by
   * `stampBevelIdsIfMissing` from `utils/bevelPassesIdentity` so
   * `wps-history-bevel-scoped` can scope generated WPS reports to a
   * specific bevel across array reorder / delete. Optional on the in-
   * memory payload type because legacy reads briefly observe entries
   * without ids before stamping runs in the load effect; persisted
   * payloads always carry an id.
   */
  id?: string
  /**
   * Per-bevel WPS Number — canonical location after issue 02. The
   * legacy `sections.identifiers.wpsNumber` is migrated onto
   * `bevelPasses[0].wpsNumber` by `migrateLegacyIdentifiersIntoPassSection`
   * at the load boundary. Optional because index 1+ bevels start
   * blank until the user picks one in the preview/generate flow.
   */
  wpsNumber?: string
  /**
   * Per-bevel WPS revision — mirrors `wpsNumber`'s storage rules.
   */
  revision?: string
  passSequence: string[]
  customPasses: Array<{ name: string; count: string }>
  rootType: RootType | ''
  fillCount: string
  capCount: string
  stripCount: string
  /**
   * Free-form note rendered in the WPS PDF's "Pass Sequence Notes" cell
   * for this bevel (slice 10f of the WPS PDF report rollout). Optional —
   * absent / blank values surface as PLACEHOLDER on the report.
   */
  notesPdf: string
  /**
   * PDF-extracted pass-sequence crop (S3 key) from WPS import. Present only
   * on imported pipe sets; used to prefer the source diagram over catalog.
   */
  importPassSequenceS3Key?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function createDefaultPassPayload(): PassPayload {
  return {
    passSequence: [],
    customPasses: [],
    rootType: '',
    fillCount: '1',
    capCount: '1',
    stripCount: '1',
    notesPdf: ''
  }
}

function normalizeStripCountInput(value: unknown): string {
  const text = String(value ?? '').trim()
  if (!text) return '1'
  const parsed = Number(text)
  if (!Number.isFinite(parsed)) return '1'
  return String(Math.max(0, Math.trunc(parsed)))
}

function normalizeCountInput(value: unknown, max: number): string {
  const text = String(value ?? '').trim()
  if (!text) return '0'
  const parsed = Number(text)
  if (!Number.isFinite(parsed)) return '0'
  return String(Math.min(max, Math.max(0, Math.trunc(parsed))))
}

function normalizeCustomPasses(
  value: unknown
): Array<{ name: string; count: string }> {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') {
        return { name: item, count: '1' }
      }
      if (!isRecord(item)) return null
      const name = String(item.name ?? '').trim()
      if (!name) return null
      const countText = String(item.count ?? '1').trim()
      return { name, count: countText || '1' }
    })
    .filter((item): item is { name: string; count: string } => Boolean(item))
}

function normalizePassPayload(value: unknown): PassPayload {
  if (!isRecord(value)) return createDefaultPassPayload()
  const passSequence = Array.isArray(value.passSequence)
    ? value.passSequence.filter(
      (pass): pass is string =>
        typeof pass === 'string' && pass.trim().length > 0
    )
    : []
  const rootType: RootType | '' = value.rootType === 'internal' || value.rootType === 'external'
    ? value.rootType
    : ''
  const id =
    typeof value.id === 'string' && value.id.trim().length > 0
      ? value.id
      : undefined
  const wpsNumber = typeof value.wpsNumber === 'string' ? value.wpsNumber : undefined
  const revision = typeof value.revision === 'string' ? value.revision : undefined
  const importPassSequenceS3Key =
    typeof value.importPassSequenceS3Key === 'string' &&
    value.importPassSequenceS3Key.trim().length > 0
      ? value.importPassSequenceS3Key.trim()
      : undefined
  return {
    ...(id !== undefined ? { id } : {}),
    ...(wpsNumber !== undefined ? { wpsNumber } : {}),
    ...(revision !== undefined ? { revision } : {}),
    ...(importPassSequenceS3Key !== undefined ? { importPassSequenceS3Key } : {}),
    passSequence,
    customPasses: normalizeCustomPasses(value.customPasses),
    rootType,
    fillCount: normalizeCountInput(value.fillCount ?? '1', MAX_FILL_COUNT),
    capCount: normalizeCountInput(value.capCount ?? '1', MAX_CAP_COUNT),
    stripCount: normalizeStripCountInput(value.stripCount ?? '1'),
    notesPdf: typeof value.notesPdf === 'string' ? value.notesPdf : ''
  }
}

function countPassesFromPayload(payload: PassPayload): number {
  let total = 0
  const selected = new Set(payload.passSequence)
  if (selected.has('Root')) total += 1
  if (selected.has('Hot')) total += 1
  if (selected.has('Fill')) {
    total += Math.min(MAX_FILL_COUNT, Math.max(0, Number(payload.fillCount || 0)))
  }
  if (selected.has('Cap')) {
    total += Math.min(MAX_CAP_COUNT, Math.max(0, Number(payload.capCount || 0)))
  }
  if (selected.has('Strip')) total += Math.max(0, Number(payload.stripCount || 0))
  if (selected.has('Root Misfire')) {
    const custom = payload.customPasses.find((pass) => pass.name === 'Root Misfire')
    total += Math.max(1, Math.trunc(Number(custom?.count || 1)))
  }
  payload.customPasses.forEach((pass) => {
    if (pass.name === 'Root Misfire') return
    if (selected.has(pass.name)) total += Math.max(0, Number(pass.count || 0))
  })
  return total
}

function normalizeBevelPasses(items: PassPayload[], bevelCount: number): PassPayload[] {
  const targetCount = Math.max(1, bevelCount)
  const next = items.slice(0, targetCount)
  while (next.length < targetCount) {
    next.push(createDefaultPassPayload())
  }
  // Newly-pushed entries from `createDefaultPassPayload` lack an id;
  // re-stamp so every entry the form ever holds in state has one.
  return stampBevelIdsIfMissing(next)
}

function serializePassState(passByBevel: PassPayload[], activeBevelTab: number): string {
  const safeActive = Math.max(0, Math.min(activeBevelTab, passByBevel.length - 1))
  const bevelPasses = passByBevel.map((pass) => ({
    ...pass,
    passCount: String(countPassesFromPayload(pass))
  }))
  const totalPassCount = bevelPasses.reduce(
    (total, pass) => total + Math.max(0, Number(pass.passCount || 0)),
    0
  )
  return JSON.stringify({
    bevelPasses,
    activeBevelIndex: safeActive,
    passCount: String(totalPassCount)
  })
}

export default function WpsPassDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const previewReturnTo = searchParams.get('returnTo')
  const { getProject, loading: projectsLoading } = useProjects()
  const { getSection, updateSection, loading: sectionsLoading, sections } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)
  useFieldFocusHandler({
    ready: !sectionsLoading,
    resolveTarget: (params) => {
      if (params.focusBevel) {
        const el = document.querySelector(
          `[data-focus-bevel="${params.focusBevel}"]`
        ) as HTMLElement | null
        if (el) return el
      }
      if (params.focusField) {
        const el = document.querySelector(
          `[data-focus-field="${params.focusField}"]`
        ) as HTMLElement | null
        if (el) return el
      }
      return null
    }
  })

  const bevelCount = useMemo(() => {
    const bevelSection = getSection('bevel')
    if (isRecord(bevelSection) && Array.isArray(bevelSection.bevels)) {
      return Math.max(1, bevelSection.bevels.length)
    }
    return 1
  }, [getSection, sections])

  const [bevelPasses, setBevelPasses] = useState<PassPayload[]>([])
  const [activeBevelTab, setActiveBevelTab] = useState(0)
  const [newPass, setNewPass] = useState('')
  const [savedPassJson, setSavedPassJson] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [diagramUrl, setDiagramUrl] = useState<string | null>(null)
  const [diagramLoading, setDiagramLoading] = useState(false)
  const [diagramMessage, setDiagramMessage] = useState('Select Root type, Fill count, and Cap count to preview the diagram.')

  const normalizedBevelPasses = useMemo(
    () => normalizeBevelPasses(bevelPasses, bevelCount),
    [bevelPasses, bevelCount]
  )
  const normalizedActiveBevelTab = Math.max(
    0,
    Math.min(activeBevelTab, Math.max(0, normalizedBevelPasses.length - 1))
  )
  const activePass = normalizedBevelPasses[normalizedActiveBevelTab] ?? createDefaultPassPayload()

  const passPayload = useMemo(
    () => serializePassState(normalizedBevelPasses, normalizedActiveBevelTab),
    [normalizedBevelPasses, normalizedActiveBevelTab]
  )
  const isDirty = useMemo(
    () => savedPassJson !== '' && passPayload !== savedPassJson,
    [passPayload, savedPassJson]
  )

  useEffect(() => {
    if (!pipeId || sectionsLoading) return
    const parsed = getSection('pass')
    let nextBevelPasses: PassPayload[] = []
    let nextActiveTab = 0

    if (isRecord(parsed)) {
      if (Array.isArray(parsed.bevelPasses)) {
        nextBevelPasses = parsed.bevelPasses.map((item) => normalizePassPayload(item))
      } else {
        nextBevelPasses = [normalizePassPayload(parsed)]
      }
      if (typeof parsed.activeBevelIndex === 'number' && Number.isFinite(parsed.activeBevelIndex)) {
        nextActiveTab = Math.max(0, Math.trunc(parsed.activeBevelIndex))
      }
    }
    // Lazy `bevelId` migration: every entry must carry a stable string
    // id so the WPS report-history grouping (issue 05) can scope rows
    // across reorder/delete. Stamping at the load boundary lets the
    // existing serialize/save path persist the ids on the next save.
    nextBevelPasses = stampBevelIdsIfMissing(nextBevelPasses)
    // Lazy per-bevel `wpsNumber` / `revision` migration: legacy
    // pipe-sets stored these at `sections.identifiers.*`. Copy them
    // onto `bevelPasses[0]` once, only when the bevel value is blank,
    // so the new per-bevel scope (issue 02 + downstream) doesn't lose
    // the pre-migration value. Idempotent — re-running on a migrated
    // payload is a no-op.
    const legacyIdentifiers = getSection('identifiers')
    if (isRecord(legacyIdentifiers)) {
      const migrated = migrateLegacyIdentifiersIntoPassSection(
        { bevelPasses: nextBevelPasses },
        {
          wpsNumber:
            typeof legacyIdentifiers.wpsNumber === 'string'
              ? legacyIdentifiers.wpsNumber
              : '',
          revision:
            typeof legacyIdentifiers.revision === 'string'
              ? legacyIdentifiers.revision
              : ''
        }
      )
      nextBevelPasses = migrated.bevelPasses as PassPayload[]
    }
    const normalized = normalizeBevelPasses(nextBevelPasses, bevelCount)
    const clampedActive = Math.min(nextActiveTab, Math.max(0, normalized.length - 1))
    setBevelPasses(normalized)
    setActiveBevelTab(clampedActive)
    setSavedPassJson(serializePassState(normalized, clampedActive))
    setSaveError('')
  }, [pipeId, sectionsLoading, getSection, sections, bevelCount])

  const rootSelected = activePass.passSequence.includes('Root')
  const fillSelected = activePass.passSequence.includes('Fill')
  const capSelected = activePass.passSequence.includes('Cap')
  const normalizedFillCount = Math.min(MAX_FILL_COUNT, Math.max(0, Number(activePass.fillCount || 0)))
  const normalizedCapCount = Math.min(MAX_CAP_COUNT, Math.max(0, Number(activePass.capCount || 0)))
  const rootType = activePass.rootType
  const rootTypeMissing = rootSelected && !rootType
  const diagramReady =
    rootSelected &&
    !!rootType &&
    fillSelected &&
    capSelected &&
    normalizedFillCount > 0 &&
    normalizedCapCount > 0

  useEffect(() => {
    let cancelled = false

    if (!diagramReady) {
      setDiagramLoading(false)
      setDiagramUrl(null)
      if (!rootSelected) {
        setDiagramMessage('Enable Root, Fill, and Cap passes to preview the diagram.')
      } else if (!rootType) {
        setDiagramMessage('Select Internal Root or External Root to preview the diagram.')
      } else if (!fillSelected || normalizedFillCount < 1) {
        setDiagramMessage('Select Fill pass with count between 1 and 21 to preview the diagram.')
      } else if (!capSelected || normalizedCapCount < 1) {
        setDiagramMessage('Select Cap pass with count between 1 and 2 to preview the diagram.')
      }
      return () => {
        cancelled = true
      }
    }

    setDiagramLoading(true)
    setDiagramMessage('Loading diagram...')
    // Prefer catalog entry registered from the PDF crop at import time;
    // fill/cap now include Strip-aware vision counts so the lookup matches.
    void fetchPassSequenceImage({
      rootType,
      capCount: normalizedCapCount,
      fillCount: normalizedFillCount
    })
      .then((response) => {
        if (cancelled) return
        if (response.imageUrl) {
          setDiagramUrl(response.imageUrl)
          setDiagramMessage('')
          return
        }
        setDiagramUrl(null)
        setDiagramMessage(
          activePass.importPassSequenceS3Key
            ? 'Extracted diagram is available from import but not yet in the catalog for this fill/cap.'
            : 'No diagram available for the selected pass configuration.'
        )
      })
      .catch(() => {
        if (cancelled) return
        setDiagramUrl(null)
        setDiagramMessage('Unable to load diagram. Please try again.')
      })
      .finally(() => {
        if (cancelled) return
        setDiagramLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    diagramReady,
    rootSelected,
    rootType,
    fillSelected,
    capSelected,
    normalizedFillCount,
    normalizedCapCount,
    activePass.importPassSequenceS3Key
  ])

  const updateActivePass = (
    updater: (current: PassPayload) => PassPayload
  ) => {
    setBevelPasses((prev) => {
      const normalized = normalizeBevelPasses(prev, bevelCount)
      return normalized.map((pass, index) => {
        if (index !== normalizedActiveBevelTab) return pass
        return updater(pass)
      })
    })
  }

  const persistPassSection = useCallback(
    async (
      nextBevelPasses: PassPayload[],
      activeIndex: number,
      options?: { manual?: boolean; showToast?: boolean }
    ): Promise<boolean> => {
      if (!pipeId || saving) return false
      if (options?.manual && rootTypeMissing) {
        setSaveError('Select Internal Root or External Root before saving.')
        return false
      }

      setSaveError('')
      const normalized = normalizeBevelPasses(nextBevelPasses, bevelCount)
      const totalPasses = normalized.reduce(
        (total, pass) => total + countPassesFromPayload(pass),
        0
      )
      const clampedActive = Math.min(activeIndex, Math.max(0, normalized.length - 1))
      setSaving(true)
      try {
        await updateSection('pass', {
          bevelPasses: normalized.map((pass) => ({
            ...pass,
            passCount: String(countPassesFromPayload(pass))
          })),
          activeBevelIndex: clampedActive,
          passCount: String(totalPasses)
        })
        setSavedPassJson(serializePassState(normalized, clampedActive))
        if (options?.showToast) toast.success('Pass details saved')
        return true
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not save pass details')
        return false
      } finally {
        setSaving(false)
      }
    },
    [pipeId, saving, bevelCount, updateSection, rootTypeMissing]
  )

  const handleSave = async (options?: { showToast?: boolean }): Promise<boolean> => {
    if (!pipeId || !isDirty || saving) return false
    const nextBevelPasses = normalizeBevelPasses(bevelPasses, bevelCount)
    return persistPassSection(nextBevelPasses, normalizedActiveBevelTab, {
      manual: true,
      showToast: options?.showToast ?? true
    })
  }

  const handleWeldDetailsClick = async (passLabel: string) => {
    if (!id || !pipeId || saving) return
    if (isDirty) {
      const saved = await handleSave({ showToast: false })
      if (!saved) return
    }
    navigate(
      `/projects/${id}/next/${pipeId}/pass/bevel/${normalizedActiveBevelTab}/${encodeURIComponent(passLabel)}`
    )
  }

  const countPasses = () => countPassesFromPayload(activePass)

  const buildPassList = () =>
    expandPassSequenceToLabels({
      passSequence: activePass.passSequence,
      customPasses: activePass.customPasses,
      fillCount: activePass.fillCount,
      capCount: activePass.capCount,
      stripCount: activePass.stripCount
    })

  const isSelected = (pass: string) => activePass.passSequence.includes(pass)
  const togglePass = (pass: string) => {
    updateActivePass((current) => {
      const exists = current.passSequence.includes(pass)
      if (!exists) {
        return { ...current, passSequence: [...current.passSequence, pass] }
      }
      const nextSequence = current.passSequence.filter((item) => item !== pass)
      if (pass === 'Root') {
        setSaveError('')
        return { ...current, passSequence: nextSequence, rootType: '' }
      }
      return { ...current, passSequence: nextSequence }
    })
  }
  const orderedPasses = [
    ...basePassOrder,
    ...activePass.customPasses
      .map((item) => item.name)
      .filter((name) => !basePassOrder.includes(name) && name !== LEGACY_ROOT_MISFIRE_PASS)
  ]
  const removeCustomPass = (name: string) => {
    updateActivePass((current) => ({
      ...current,
      customPasses: current.customPasses.filter((item) => item.name !== name),
      passSequence: current.passSequence.filter((item) => item !== name)
    }))
  }
  if (id && projectsLoading) {
    return <PageLoader label="Loading project…" />
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Pass Details not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  if (sectionsLoading || savedPassJson === '') {
    return <PageLoader label="Loading pass details…" />
  }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={previewReturnTo} contextLabel="Pass Sequence" />
      <PageHeader
        backTo={
          previewReturnTo
            ? { to: previewReturnTo, label: 'Back' }
            : id && pipeId
              ? { to: `/projects/${id}/next/${pipeId}`, label: 'Back' }
              : { onClick: () => navigate(-1), label: 'Back' }
        }
        title={<ProjectPageTitle label="Pass Details" projectName={project.name} />}
        subtitle={
          <>
            {formatClientLocationSubtitle(project) ? (
              <span>{formatClientLocationSubtitle(project)}</span>
            ) : null}
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving || rootTypeMissing} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {normalizedBevelPasses.map((_, index) => {
            const isActive = index === normalizedActiveBevelTab
            return (
              <button
                key={`bevel-tab-${index + 1}`}
                type="button"
                onClick={() => setActiveBevelTab(index)}
                className={`min-w-[8rem] border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Bevel {index + 1}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <div className="rounded-lg border border-gray-200 bg-white">
            <p className="px-3 pt-3 text-xs font-medium text-gray-500">Pass Sequence</p>
            <div className="divide-y divide-gray-100">
              {orderedPasses.map((pass) => (
                <div key={pass} className="px-3 py-2">
                  {pass === 'Root' ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected('Root')}
                          onChange={() => togglePass('Root')}
                          aria-label="Include Root pass"
                          className="h-4 w-4 shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                        />
                        <span className="text-sm text-gray-700">Root</span>
                      </div>
                      {isSelected('Root') ? (
                        <div
                          role="radiogroup"
                          aria-label="Root type"
                          className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-gray-700"
                        >
                          <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap">
                            <input
                              type="radio"
                              name="wps-root-type"
                              checked={rootType === 'internal'}
                              onChange={() => {
                                updateActivePass((current) => ({ ...current, rootType: 'internal' }))
                                setSaveError('')
                              }}
                              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            Internal Root
                          </label>
                          <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap">
                            <input
                              type="radio"
                              name="wps-root-type"
                              checked={rootType === 'external'}
                              onChange={() => {
                                updateActivePass((current) => ({ ...current, rootType: 'external' }))
                                setSaveError('')
                              }}
                              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            External Root
                          </label>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                      <label className="flex min-w-0 cursor-pointer items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={isSelected(pass)}
                          onChange={() => togglePass(pass)}
                          className="h-4 w-4 shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                        />
                        <span className="truncate">{pass}</span>
                      </label>
                      <div className="flex items-center gap-2">
                        {pass === 'Fill' && isSelected('Fill') && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-400">Count</span>
                            <input
                              className="input-primary h-8 w-20 text-center"
                              value={activePass.fillCount}
                              onChange={(event) => {
                                updateActivePass((current) => ({
                                  ...current,
                                  fillCount: normalizeCountInput(event.target.value, MAX_FILL_COUNT)
                                }))
                              }}
                              type="number"
                              min={0}
                              max={MAX_FILL_COUNT}
                            />
                          </div>
                        )}
                        {pass === 'Cap' && isSelected('Cap') && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-400">Count</span>
                            <input
                              className="input-primary h-8 w-20 text-center"
                              value={activePass.capCount}
                              onChange={(event) => {
                                updateActivePass((current) => ({
                                  ...current,
                                  capCount: normalizeCountInput(event.target.value, MAX_CAP_COUNT)
                                }))
                              }}
                              type="number"
                              min={0}
                              max={MAX_CAP_COUNT}
                            />
                          </div>
                        )}
                        {pass === 'Strip' && isSelected('Strip') && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-400">Count</span>
                            <input
                              className="input-primary h-8 w-20 text-center"
                              value={activePass.stripCount}
                              onChange={(event) => {
                                updateActivePass((current) => ({
                                  ...current,
                                  stripCount: event.target.value
                                }))
                              }}
                              type="number"
                              min={0}
                            />
                          </div>
                        )}
                        {activePass.customPasses.some((item) => item.name === pass) && (
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center rounded-md p-1 text-error-600 transition hover:bg-error-50"
                              title={`Remove custom pass “${pass}”`}
                              aria-label={`Remove custom pass ${pass}`}
                              onClick={() => removeCustomPass(pass)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                            {isSelected(pass) && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-gray-400">Count</span>
                                <input
                                  className="input-primary h-8 w-20 text-center"
                                  value={activePass.customPasses.find((item) => item.name === pass)?.count || '1'}
                                  onChange={(event) => {
                                    const value = event.target.value
                                    updateActivePass((current) => ({
                                      ...current,
                                      customPasses: current.customPasses.map((item) =>
                                        item.name === pass ? { ...item, count: value } : item
                                      )
                                    }))
                                  }}
                                  type="number"
                                  min={0}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 px-3 py-2 text-[11px] text-gray-500">
              {/* Fill count supports up to {MAX_FILL_COUNT}. Cap count supports up to {MAX_CAP_COUNT}. */}
            </div>
            <div className="border-t border-gray-100 px-3 py-2">
              <div className="flex items-center gap-2">
                <input
                  className="input-primary h-8 flex-1"
                  placeholder="Add custom pass"
                  value={newPass}
                  onChange={(event) => setNewPass(event.target.value)}
                />
                <Button
                  type="button"
                  className="h-8 px-3 text-xs"
                  onClick={() => {
                    const value = newPass.trim()
                    if (!value) return
                    updateActivePass((current) => {
                      const nextCustom = !current.customPasses.some((item) => item.name === value) && !basePassOrder.includes(value)
                        ? [...current.customPasses, { name: value, count: '1' }]
                        : current.customPasses
                      const nextSequence = current.passSequence.includes(value)
                        ? current.passSequence
                        : [...current.passSequence, value]
                      return {
                        ...current,
                        customPasses: nextCustom,
                        passSequence: nextSequence
                      }
                    })
                    setNewPass('')
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
            {(rootTypeMissing || saveError) && (
              <p className="border-t border-gray-100 px-3 py-2 text-xs text-error-600">
                {saveError || 'Select Internal Root or External Root before saving.'}
              </p>
            )}
          </div>
        </div>
        <div className="self-stretch rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-medium text-gray-500">Pass Sequence Diagram</p>
          <div className="mt-3 flex items-center justify-center mx-10 my-10">
            {diagramUrl ? (
              <img
                src={diagramUrl}
                alt="Pass Sequence Diagram"
                className="mx-10 my-10 rounded-md object-contain shadow-sm"
              />
            ) : (
              <div className="flex h-44 w-44 items-center justify-center rounded-md bg-white px-4 text-center text-xs text-gray-500">
                {diagramLoading ? 'Loading diagram...' : diagramMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold text-gray-700">
          Pass-Sequence Notes (rendered on WPS PDF for this bevel)
        </p>
        <textarea
          className="input-primary mt-2 min-h-[60px]"
          value={activePass.notesPdf}
          onChange={(event) =>
            updateActivePass((current) => ({ ...current, notesPdf: event.target.value }))
          }
          placeholder="e.g. First hot pass deposited within 5 min of root completion"
          data-testid="pass-notes-pdf-input"
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700">Pass list</p>
          <span className="text-[11px] text-gray-400">
            Bevel {normalizedActiveBevelTab + 1}: {countPasses()} passes
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {buildPassList().length === 0 && (
            <p className="text-xs text-gray-400">Select a pass sequence to generate the list.</p>
          )}
          {buildPassList().map((pass) => (
            <div key={pass} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
              <span className="text-sm text-gray-700">{pass}</span>
              <Button
                className="h-8 px-2.5 text-xs"
                type="button"
                loading={saving}
                disabled={saving}
                onClick={() => void handleWeldDetailsClick(pass)}
              >
                Weld Details
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
